import React, { useCallback, useEffect, useState } from 'react';

// Banquet Pricing Advisor — kicks off an AI-generated set of rate
// recommendations for the next N days, segmented by event type.
// Backend: aiFeatures.js /banquet-pricing-advisor/{generate, recommendations,
// recommendations/:id/apply, recommendations/:id/reject}.

const sectionStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '1rem 1.2rem',
  marginTop: '0.7rem',
};

const EVENT_TYPES = [
  { value: 'WEDDING', label: 'Wedding' },
  { value: 'CORPORATE', label: 'Corporate' },
  { value: 'SOCIAL', label: 'Social' },
];

const fmtCurrency = (n) => {
  if (n == null || n === '') return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const fmtPct = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};

const demandColor = (d) => {
  switch (String(d || '').toUpperCase()) {
    case 'HIGH': return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
    case 'MEDIUM': return { bg: '#fef9c3', text: '#854d0e', border: '#fde047' };
    case 'LOW': return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' };
    default: return { bg: '#f3f4f6', text: '#475569', border: '#e5e7eb' };
  }
};

function BanquetPricingAdvisor({ token, selectedProperty }) {
  const [days, setDays] = useState(60);
  const [selectedTypes, setSelectedTypes] = useState(['WEDDING', 'CORPORATE', 'SOCIAL']);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState('PENDING');
  const [recommendations, setRecommendations] = useState([]);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState(null);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  const load = useCallback(async (status) => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/ai/banquet-pricing-advisor/recommendations?status=${status || tab}&limit=100`, { headers: headers() });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to load recommendations');
      setRecommendations(Array.isArray(body.recommendations) ? body.recommendations : []);
    } catch (e) {
      setError(e?.message || 'Failed to load');
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty?.id, tab, headers]);

  useEffect(() => { load(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedProperty?.id, tab]);

  const toggleType = (t) => {
    setSelectedTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const generate = async () => {
    if (!selectedTypes.length) { setError('Select at least one event type.'); return; }
    setRunning(true);
    setError('');
    setInfo('');
    setSummary('');
    try {
      const res = await fetch('/api/v1/ai/banquet-pricing-advisor/generate', {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ days, event_types: selectedTypes }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to generate');
      setSummary(body.summary || '');
      setInfo(`${body.recommendations_count} recommendation${body.recommendations_count === 1 ? '' : 's'} generated.`);
      setTab('PENDING');
      await load('PENDING');
    } catch (e) {
      setError(e?.message || 'Failed to generate recommendations');
    } finally {
      setRunning(false);
    }
  };

  const apply = async (id) => {
    const remarks = window.prompt('Optional remarks (apply):', '') || '';
    setActioningId(id);
    setError('');
    try {
      const res = await fetch(`/api/v1/ai/banquet-pricing-advisor/recommendations/${id}/apply`, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ remarks }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Apply failed');
      setInfo('Recommendation applied — own-rate calendar updated.');
      await load(tab);
    } catch (e) {
      setError(e?.message || 'Apply failed');
    } finally {
      setActioningId(null);
    }
  };

  const reject = async (id) => {
    const remarks = window.prompt('Optional remarks (reject):', '') || '';
    setActioningId(id);
    setError('');
    try {
      const res = await fetch(`/api/v1/ai/banquet-pricing-advisor/recommendations/${id}/reject`, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ remarks }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Reject failed');
      setInfo('Recommendation rejected.');
      await load(tab);
    } catch (e) {
      setError(e?.message || 'Reject failed');
    } finally {
      setActioningId(null);
    }
  };

  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: '1rem', maxWidth: 1200 }}>
        <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Banquet Pricing Advisor</h2>
        <p style={{ color: '#6b7280' }}>Please select a property to use the pricing advisor.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: 1300 }}>
      <div style={{ marginBottom: '0.6rem' }}>
        <h2 style={{ margin: 0, color: 'var(--theme-primary)' }}>Banquet Pricing Advisor</h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem' }}>
          AI-generated per-plate rate recommendations using your booking history, competitor rates, demand forecast, and search trends. Every recommendation is logged for audit.
        </p>
      </div>

      {error ? <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{error}</div> : null}
      {info ? <div className="success-banner" style={{ marginBottom: '0.6rem' }}>{info}</div> : null}

      {/* Run-form */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>Forecast window</div>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #cbd5e0' }}>
              {[14, 30, 60, 90, 120, 180].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>Event types</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {EVENT_TYPES.map((t) => {
                const on = selectedTypes.includes(t.value);
                return (
                  <button key={t.value} onClick={() => toggleType(t.value)}
                    style={{
                      padding: '0.35rem 0.8rem',
                      borderRadius: 6,
                      border: `1px solid ${on ? 'var(--theme-primary)' : '#cbd5e0'}`,
                      background: on ? 'var(--theme-primary, #1e40af)' : '#fff',
                      color: on ? '#fff' : '#374151',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}>{t.label}</button>
                );
              })}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button onClick={generate} disabled={running}
              className="btn-primary"
              style={{
                padding: '0.55rem 1.1rem', border: 'none', borderRadius: 6, fontWeight: 700,
                cursor: running ? 'wait' : 'pointer', background: 'var(--theme-primary, #1e40af)', color: '#fff',
              }}>{running ? 'Generating…' : 'Run advisor'}</button>
          </div>
        </div>
        {summary ? (
          <div style={{ marginTop: '0.7rem', padding: '0.6rem 0.8rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: '0.88rem' }}>
            <span style={{ fontWeight: 700, color: '#1e40af' }}>AI summary:</span> {summary}
          </div>
        ) : null}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.8rem' }}>
        {['PENDING', 'APPLIED', 'REJECTED', 'ALL'].map((s) => (
          <button key={s} onClick={() => setTab(s)}
            style={{
              padding: '0.4rem 0.9rem',
              border: '1px solid #cbd5e0', borderRadius: 6,
              background: tab === s ? 'var(--theme-primary, #1e40af)' : '#fff',
              color: tab === s ? '#fff' : '#374151',
              cursor: 'pointer', fontWeight: 600,
            }}>{s.charAt(0)}{s.slice(1).toLowerCase()}</button>
        ))}
        <button onClick={() => load(tab)} disabled={loading}
          style={{
            padding: '0.4rem 0.9rem', border: '1px solid #cbd5e0', borderRadius: 6,
            background: '#fff', color: '#374151', cursor: 'pointer', marginLeft: 'auto',
          }}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      {/* Table */}
      <div style={sectionStyle}>
        {loading && recommendations.length === 0 ? (
          <div style={{ color: '#6b7280' }}>Loading…</div>
        ) : recommendations.length === 0 ? (
          <div style={{ color: '#6b7280' }}>No {tab.toLowerCase()} recommendations. Run the advisor above to generate fresh advice.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark, #1e3a5f)', color: '#fff' }}>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Date Range</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Event Type</th>
                  <th style={{ textAlign: 'right', padding: '0.55rem' }}>Current Veg / Non-veg</th>
                  <th style={{ textAlign: 'right', padding: '0.55rem' }}>Suggested Veg / Non-veg</th>
                  <th style={{ textAlign: 'right', padding: '0.55rem' }}>Δ%</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Demand</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Confidence</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Reasoning</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.map((r) => {
                  const dc = demandColor(r.demand_level);
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                      <td style={{ padding: '0.55rem' }}>
                        <div style={{ fontWeight: 600 }}>{r.date_range_start}</div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>to {r.date_range_end}</div>
                      </td>
                      <td style={{ padding: '0.55rem', fontWeight: 600 }}>{r.event_type}</td>
                      <td style={{ padding: '0.55rem', textAlign: 'right' }}>{fmtCurrency(r.current_rate_veg)} / {fmtCurrency(r.current_rate_nonveg)}</td>
                      <td style={{ padding: '0.55rem', textAlign: 'right', fontWeight: 700, color: 'var(--theme-primary)' }}>{fmtCurrency(r.suggested_rate_veg)} / {fmtCurrency(r.suggested_rate_nonveg)}</td>
                      <td style={{ padding: '0.55rem', textAlign: 'right', fontWeight: 600, color: Number(r.variance_pct) > 0 ? '#166534' : Number(r.variance_pct) < 0 ? '#991b1b' : '#6b7280' }}>{fmtPct(r.variance_pct)}</td>
                      <td style={{ padding: '0.55rem' }}>
                        <span style={{ display: 'inline-block', background: dc.bg, color: dc.text, border: `1px solid ${dc.border}`, padding: '0.15rem 0.55rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 700 }}>{r.demand_level || '—'}</span>
                      </td>
                      <td style={{ padding: '0.55rem' }}>{r.confidence || '—'}</td>
                      <td style={{ padding: '0.55rem', maxWidth: 280, fontSize: '0.82rem', color: '#374151' }}>{r.reasoning || '—'}</td>
                      <td style={{ padding: '0.55rem' }}>
                        <span style={{
                          padding: '0.15rem 0.55rem', borderRadius: 4, fontSize: '0.76rem', fontWeight: 700,
                          background: r.status === 'PENDING' ? '#dbeafe' : r.status === 'APPLIED' ? '#dcfce7' : r.status === 'REJECTED' ? '#fee2e2' : '#f3f4f6',
                          color: r.status === 'PENDING' ? '#1e40af' : r.status === 'APPLIED' ? '#166534' : r.status === 'REJECTED' ? '#991b1b' : '#475569',
                        }}>{r.status}</span>
                        {r.responded_by_username ? (
                          <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>
                            by {r.responded_by_username}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: '0.55rem', whiteSpace: 'nowrap' }}>
                        {r.status === 'PENDING' ? (
                          <>
                            <button onClick={() => apply(r.id)} disabled={actioningId === r.id}
                              className="btn-success"
                              style={{ padding: '0.3rem 0.7rem', borderRadius: 4, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontWeight: 600, marginRight: 6 }}>Apply</button>
                            <button onClick={() => reject(r.id)} disabled={actioningId === r.id}
                              className="btn-danger"
                              style={{ padding: '0.3rem 0.7rem', borderRadius: 4, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Reject</button>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: '0.8rem', fontSize: '0.78rem', color: '#6b7280' }}>
        Apply writes the suggested veg/non-veg rate into your own-rate calendar (same store as Competitor Intelligence) for the recommendation's date range and event type. Rejecting keeps the recommendation in history with your remark.
      </div>
    </div>
  );
}

export default BanquetPricingAdvisor;
